import { Tinode } from '@inno-sdk/livechat-sdk'
import { AppId } from '@inno-tools/container-bridge'
import { CustomEvents } from 'shared/constants/customEvents'
import { listenCustomEvent } from 'shared/utils/customEvents'

/**
 * How long to wait for the live chat session before giving up on it. The
 * session may never arrive — the widget might not be mounted on this page, or
 * the connection may be down — and a caller stuck forever is worse than one
 * that carries on without a conversation.
 */
export const LIVE_CHAT_SESSION_TIMEOUT_MS = 5000

/**
 * Waits for LivechatWidgets' LIVECHAT_SESSION_READY signal, since opening a
 * chat can race the SDK's own login handshake. Resolves at once when there is
 * no SDK instance or the session is already authenticated, and always resolves
 * — never rejects — after {@link LIVE_CHAT_SESSION_TIMEOUT_MS} so callers can
 * fall back rather than hang.
 */
export const waitForLiveChatSession = (
  livechat?: typeof Tinode | null
): Promise<void> => {
  if (!livechat || livechat.isAuthenticated()) return Promise.resolve()

  return new Promise(resolve => {
    let stopListening = () => {}

    // Safe to close over `timer` before it is assigned: settle only ever runs
    // from the timer itself or from a listener attached after this line.
    const settle = () => {
      clearTimeout(timer)
      stopListening()
      resolve()
    }

    const timer = setTimeout(settle, LIVE_CHAT_SESSION_TIMEOUT_MS)
    stopListening = listenCustomEvent(
      CustomEvents.LIVECHAT_SESSION_READY,
      settle
    )
  })
}

export const getTopicIdFromAuthId = async (
  livechat?: typeof Tinode | null,
  authId?: string | number | null
): Promise<string | null> => {
  if (!livechat || !authId) return null

  try {
    const reply = await livechat.getTopicFromAuthId(authId)
    return reply?.topic ?? reply?.params?.topic ?? null
  } catch {
    return null
  }
}

/** The Live Chat app's own url, with no conversation selected. */
export const getLiveChatAppUrl = async (
  baseUrl: string = window.location.origin
): Promise<string> => {
  // Dynamic import avoids a static cycle with appConfigs, which transitively
  // depends on this module's consumers.
  const { appConfigs } = await import('shared/configs/appConfigs')
  const liveChatPath = appConfigs[AppId.LIVE_CHAT].path
  return `${baseUrl.replace(/\/+$/, '')}${liveChatPath}`
}

export const getLiveChatUrl = async (
  topicId: string | null | undefined,
  baseUrl: string = window.location.origin
): Promise<string | null> => {
  if (!topicId) return null

  return `${await getLiveChatAppUrl(baseUrl)}#/${topicId}`
}
